import { cx } from "@emotion/css";
import { InputAdornment } from "@mui/material";
import { useFormik } from "formik";
import { FilePenLine, Pencil } from "lucide-react";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import {
	PrimaryButton,
	TextPrimaryButton,
} from "src/components/Default/LekUI/Button";
import { FormTextField } from "src/components/Default/LekUI/TextField";
import {
	Modal,
	ModalFooter,
	ModalHeader,
} from "src/components/Modals/Components/ModalsComponents";
import { usePatchExam } from "src/hooks/useExam";
import { useFontSize } from "src/hooks/useFontSize";
import * as Yup from "yup";

export interface FinishExamData {
	id: string;
	name: string;
	courseId?: string;
	type: string;
	stats: {
		totalQuestions: number;
		resolvedQuestions: number;
	};
}

interface FinishExamDefaultModalProps {
	onClose: () => void;
	exam: FinishExamData;
	titleText: string;
	/** Linia informacyjna pod nazwą egzaminu (ikona + tekst). */
	info: ReactNode;
	/** Opcjonalna treść między info a stopką (np. kafelki wyboru). */
	children?: ReactNode;
	primaryText: string;
	onPrimary: () => void | Promise<void>;
}

export const FinishExamDefaultModal = ({
	onClose,
	exam,
	titleText,
	info,
	children,
	primaryText,
	onPrimary,
}: FinishExamDefaultModalProps) => {
	const { t } = useTranslation();
	const { modalTextFontSizeClass, modalHeaderFontSizeClass } = useFontSize();
	const { mutateAsync: patchExam } = usePatchExam() as unknown as {
		mutateAsync: (values: { id: string; name: string }) => Promise<void>;
	};

	const { values, errors, setFieldValue, submitForm, isSubmitting } = useFormik(
		{
			initialValues: { name: exam.name },
			validationSchema: Yup.object().shape({
				name: Yup.string()
					.trim()
					.required(
						t(
							"modals.finishExam.examNameRequired",
							"Nazwa egzaminu nie może być pusta.",
						),
					),
			}),
			onSubmit: async ({ name }) => {
				const trimmed = name.trim();
				if (trimmed !== exam.name) {
					await patchExam({ id: exam.id, name: trimmed });
				}
				await onPrimary();
			},
		},
	);

	return (
		<Modal onClose={onClose} maxWidth={720}>
			<div className="finish-exam-modal">
				<ModalHeader Icon={FilePenLine} title={titleText} hasCloseButton />

				<div className="flex flex-col gap-8 w-100">
					<label
						htmlFor="finish-exam-name"
						className={cx(modalHeaderFontSizeClass, "text-black")}
					>
						{t("modals.finishExam.examName", "Nazwa egzaminu")}
					</label>
					<FormTextField
						id="finish-exam-name"
						margin="none"
						fullWidth
						placeholder={t(
							"modals.finishExam.examNamePlaceholder",
							"Wpisz nazwę egzaminu",
						)}
						value={values.name}
						error={errors.name}
						onChange={(e) => setFieldValue("name", e.target.value)}
						slotProps={{
							htmlInput: { "data-test-id": "finish-exam-name-input" },
							input: {
								endAdornment: (
									<InputAdornment position="end">
										<Pencil className="icon-small text-dark-grey" />
									</InputAdornment>
								),
							},
						}}
					/>
				</div>

				<div
					className={cx(
						"flex items-center gap-8 text-dark-grey",
						modalTextFontSizeClass,
					)}
				>
					{info}
				</div>

				{children}

				<ModalFooter>
					<TextPrimaryButton
						data-test-id="finish-exam-cancel-button"
						onClick={onClose}
					>
						{t("commands.cancel")}
					</TextPrimaryButton>
					<PrimaryButton
						data-test-id="finish-exam-submit-button"
						isLoading={isSubmitting}
						disabled={!!errors.name}
						onClick={submitForm}
					>
						{primaryText}
					</PrimaryButton>
				</ModalFooter>
			</div>
		</Modal>
	);
};
